import type { JssStyle } from 'jss';
import type { WebLegacyWidgetEntity } from '@web/types';
import type { TimerWidgetData } from '@web/widgets/timer/type/timer-widget.type';

import { ThemeColorNames } from '@sites/site-theme/src/enums/color-names.enum';
import { commonFontSizeCoefficient, themePStyles } from '@sites/site-theme/src/styles/text.styles';
import { themeColorCss } from '@sites/site-theme/src/utils/variables';

import { EHorizontalPosition, ESize, ETimerAppearanceSeparator } from '@common/enums';

function commonColonSeparator(element: WebLegacyWidgetEntity<TimerWidgetData>): JssStyle {
    return {
        content: '""',
        position: 'absolute',
        display: 'block',
        background: element.data.dividerColor || themeColorCss(ThemeColorNames.dark),
    };
}

function commonLineSeparator(element: WebLegacyWidgetEntity<TimerWidgetData>): JssStyle {
    return {
        content: '""',
        position: 'absolute',
        height: '50%',
        top: '15%',
        width: '1px',
        display: 'block',
        background: element.data.dividerColor || themeColorCss(ThemeColorNames.primary),
    };
}

function separatorS(element: WebLegacyWidgetEntity<TimerWidgetData>): JssStyle {
    const commonSeparatorStyles = {
        position: 'relative',
        margin: '0 2px',
    };

    switch (element.data.appearance) {
        case ETimerAppearanceSeparator.none:
            return { ...commonSeparatorStyles };
        case ETimerAppearanceSeparator.colon:
            return {
                ...commonSeparatorStyles,
                '&:before': {
                    ...commonColonSeparator(element),
                    top: 'calc(50% - 14px)',
                    width: '2px',
                    height: '2px',
                },
                '&:after': {
                    ...commonColonSeparator(element),
                    top: 'calc(50% - 5px)',
                    width: '2px',
                    height: '2px',
                },
            };
        case ETimerAppearanceSeparator.line:
            return {
                ...commonSeparatorStyles,
                '&:before': { ...commonLineSeparator(element) },
            };
        default:
            return { ...commonSeparatorStyles };
    }
}

function separatorM(element: WebLegacyWidgetEntity<TimerWidgetData>): JssStyle {
    const commonSeparatorStyles = {
        position: 'relative',
        margin: '0 4px',
    };

    switch (element.data.appearance) {
        case ETimerAppearanceSeparator.none:
            return { ...commonSeparatorStyles };
        case ETimerAppearanceSeparator.colon:
            return {
                ...commonSeparatorStyles,
                '&:before': {
                    ...commonColonSeparator(element),
                    top: 'calc(50% - 20px)',
                    width: '3px',
                    height: '3px',
                },
                '&:after': {
                    ...commonColonSeparator(element),
                    top: 'calc(50% - 5px)',
                    width: '3px',
                    height: '3px',
                },
            };
        case ETimerAppearanceSeparator.line:
            return {
                ...commonSeparatorStyles,
                '&:before': { ...commonLineSeparator(element) },
            };
        default:
            return { ...commonSeparatorStyles };
    }
}

function separatorL(element: WebLegacyWidgetEntity<TimerWidgetData>): JssStyle {
    const commonSeparatorStyles = {
        position: 'relative',
        margin: '0 6px',
    };

    switch (element.data.appearance) {
        case ETimerAppearanceSeparator.none:
            return { ...commonSeparatorStyles };
        case ETimerAppearanceSeparator.colon:
            return {
                ...commonSeparatorStyles,
                '&:before': {
                    ...commonColonSeparator(element),
                    top: 'calc(50% - 20px)',
                    width: '4px',
                    height: '4px',
                },
                '&:after': {
                    ...commonColonSeparator(element),
                    top: 'calc(50% - 5px)',
                    width: '4px',
                    height: '4px',
                },
            };
        case ETimerAppearanceSeparator.line:
            return {
                ...commonSeparatorStyles,
                '&:before': { ...commonLineSeparator(element) },
            };
        default:
            return { ...commonSeparatorStyles };
    }
}

function separatorStyles(element: WebLegacyWidgetEntity<TimerWidgetData>): JssStyle {
    switch (element.data.size) {
        case ESize.S:
            return separatorS(element);
        case ESize.M:
            return separatorM(element);
        case ESize.L:
            return separatorL(element);
        default:
            return separatorM(element);
    }
}

function getJustifyContent(horizontalPosition: EHorizontalPosition) {
    switch (horizontalPosition) {
        case EHorizontalPosition.L:
            return { justifyContent: 'flex-start' };
        case EHorizontalPosition.M:
            return { justifyContent: 'center' };
        case EHorizontalPosition.R:
            return { justifyContent: 'flex-end' };
        default:
            return { justifyContent: 'normal' };
    }
}

export function getStyles(element: WebLegacyWidgetEntity<TimerWidgetData>, { isConstructor }: any): JssStyle {
    const { data } = element;
    const fontSizeCoefficient = commonFontSizeCoefficient[element.data.size];
    const mobileFontSizeCoefficient = commonFontSizeCoefficient[element.data.mobileSize];
    const tabletFontSizeCoefficient = commonFontSizeCoefficient[element.data.tabletSize ?? element.data.mobileSize];

    let wrapColumns: any = {};
    let value: JssStyle = {};
    let label: JssStyle = {};
    let expiredText: JssStyle = {};

    if (!element.data.mobileHorizontalAlign) {
        wrapColumns = {
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            ...getJustifyContent(element.data.horizontalAlign),
            '@media (max-width: 992px)': {
                ...getJustifyContent(element.data.horizontalAlign),
            },
        };

        value = {
            ...themePStyles,
            minWidth: '40px',
            textAlign: 'center',
            fontWeight: 'bold',
            fontSize: `calc(50px * ${fontSizeCoefficient})`,
            color: element.data.textColor,
            '@media (max-width: 992px)': {
                fontSize: `calc(50px * ${fontSizeCoefficient})`,
            },
        };

        label = {
            ...themePStyles,
            overflow: 'hidden',
            wordWrap: 'break-word',
            textAlign: 'center',
            fontSize: `calc(15px * ${fontSizeCoefficient})`,
            color: element.data.textColor,
            '@media (max-width: 992px)': {
                fontSize: `calc(15px * ${fontSizeCoefficient})`,
            },
        };

        expiredText = {
            ...themePStyles,
            fontSize: `calc(${themePStyles.fontSize} * ${fontSizeCoefficient})`,
            color: element.data.textColor,
            '@media (max-width: 992px)': {
                fontSize: `calc(${themePStyles.fontSize} * ${fontSizeCoefficient})`,
            },
        };
    } else {
        const tabletHorizontalAlign = element.data.tabletHorizontalAlign ?? element.data.mobileHorizontalAlign;
        wrapColumns = {
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            ...getJustifyContent(element.data.horizontalAlign),
            '@media (max-width: 992px)': {
                ...getJustifyContent(element.data.mobileHorizontalAlign),
            },
            '@media (min-width: 481px) and (max-width: 992px)': {
                ...getJustifyContent(tabletHorizontalAlign),
            },
            '&.mobileWidgetViewWrap': {
                ...getJustifyContent(element.data.mobileHorizontalAlign),
            },
            '&.tabletWidgetViewWrap': {
                ...getJustifyContent(tabletHorizontalAlign),
            },
        };

        value = {
            ...themePStyles,
            minWidth: '40px',
            textAlign: 'center',
            fontWeight: 'bold',
            fontSize: `calc(50px * ${fontSizeCoefficient})`,
            color: element.data.textColor,
            '@media (max-width: 992px)': {
                fontSize: `calc(50px * ${mobileFontSizeCoefficient})`,
            },
            '@media (min-width: 481px) and (max-width: 992px)': {
                fontSize: `calc(50px * ${tabletFontSizeCoefficient})`,
            },
            '&.mobileWidgetViewElement': {
                fontSize: `calc(50px * ${mobileFontSizeCoefficient})`,
            },
            '&.tabletWidgetViewElement': {
                fontSize: `calc(50px * ${tabletFontSizeCoefficient})`,
            },
        };

        label = {
            ...themePStyles,
            overflow: 'hidden',
            wordWrap: 'break-word',
            textAlign: 'center',
            fontSize: `calc(15px * ${fontSizeCoefficient})`,
            color: element.data.textColor,
            '@media (max-width: 992px)': {
                fontSize: `calc(15px * ${mobileFontSizeCoefficient})`,
            },
            '@media (min-width: 481px) and (max-width: 992px)': {
                fontSize: `calc(15px * ${tabletFontSizeCoefficient})`,
            },
            '&.mobileWidgetViewElement': {
                fontSize: `calc(15px * ${mobileFontSizeCoefficient})`,
            },
            '&.tabletWidgetViewElement': {
                fontSize: `calc(15px * ${tabletFontSizeCoefficient})`,
            },
        };

        expiredText = {
            ...themePStyles,
            fontSize: `calc(${themePStyles.fontSize} * ${fontSizeCoefficient})`,
            color: element.data.textColor,
            '@media (max-width: 992px)': {
                fontSize: `calc(${themePStyles.fontSize} * ${mobileFontSizeCoefficient})`,
            },
            '@media (min-width: 481px) and (max-width: 992px)': {
                fontSize: `calc(${themePStyles.fontSize} * ${tabletFontSizeCoefficient})`,
            },
            '&.mobileWidgetViewElement': {
                fontSize: `calc(15px * ${mobileFontSizeCoefficient})`,
            },
            '&.tabletWidgetViewElement': {
                fontSize: `calc(15px * ${tabletFontSizeCoefficient})`,
            },
        };
    }

    if (!isConstructor) {
        if (data?.hideOnMobile) {
            wrapColumns['@media (max-width: 480px)'] = { display: 'none' };
        }

        if (data?.hideOnTablet) {
            wrapColumns['@media (min-width: 481px) and (max-width: 992px)'] = { display: 'none' };
        }

        if (data?.hideOnDesktop) {
            wrapColumns['@media (min-width: 993px)'] = { display: 'none' };
        }
    }

    return {
        wrapColumns,
        wrapColumn: {
            display: 'flex',
            '&:last-child .separator': {
                display: 'none',
            },
        },
        column: {
            minWidth: '80px',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            color: element.data.textColor,
        },
        value,
        label,
        expiredText,
        separator: separatorStyles(element),
    };
}
